import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { storage } from '@/lib/storage';
import { Download, Home, LogOut, Menu, Search, User, X, Smartphone, Settings } from 'lucide-react';
import { LanguageSelectorNew } from '@/components/LanguageSelectorNew';
import { InstallPWAModal } from '@/components/InstallPWAModal';

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(storage.getUser());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll for glassmorphism effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if app is already installed
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      setShowInstallModal(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = () => {
    setShowInstallModal(true);
  };

  useEffect(() => {
    const checkAuth = () => {
      setUser(storage.getUser());
    };
    checkAuth();
    window.addEventListener('storage', checkAuth);
    window.addEventListener('authChanged', checkAuth);
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('authChanged', checkAuth);
    };
  }, []);

  const handleLogout = () => {
    storage.clearAuth();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const NavLinks = () => (
    <>
      <Link
        to="/search"
        className={`relative text-sm font-medium transition-colors px-3 py-2 rounded-lg ${
          isActive('/search') 
            ? 'text-secondary bg-secondary/10' 
            : 'hover:text-secondary hover:bg-secondary/5'
        }`}
        onClick={() => setMobileMenuOpen(false)}
        data-testid="navbar-search-link"
      >
        {t('navigation.searchProperties')}
        {isActive('/search') && (
          <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-secondary rounded-full" />
        )}
      </Link>
      {user && user.role !== 'admin' && (
        <Link
          to="/host/dashboard"
          className={`relative text-sm font-medium transition-colors px-3 py-2 rounded-lg ${
            isActive('/host/dashboard') 
              ? 'text-secondary bg-secondary/10' 
              : 'hover:text-secondary hover:bg-secondary/5'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        >
          {t('navigation.myProperties')}
        </Link>
      )}
      {user && user.role === 'admin' && (
        <Link
          to="/admin"
          className={`relative text-sm font-medium transition-colors px-3 py-2 rounded-lg ${
            isActive('/admin') 
              ? 'text-secondary bg-secondary/10' 
              : 'hover:text-secondary hover:bg-secondary/5'
          }`}
          onClick={() => setMobileMenuOpen(false)}
          data-testid="navbar-admin-link"
        >
          {i18n.language === 'pt' || i18n.language === 'pt-BR' ? 'Painel Admin' : 'Admin Dashboard'}
        </Link>
      )}
      <Link
        to="/host/create-property"
        className={`relative text-sm font-medium transition-colors px-3 py-2 rounded-lg ${
          isActive('/host/create-property') || isActive('/anunciar')
            ? 'text-secondary bg-secondary/10' 
            : 'hover:text-secondary hover:bg-secondary/5'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      >
        {t('navigation.plans')}
      </Link>
    </>
  );

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'glass shadow-lg border-b border-white/20'
            : 'bg-white/80 backdrop-blur-sm border-b border-transparent'
        }`}
        data-testid="navbar"
        style={{ transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)' }}
      >
        <div className="container mx-auto px-2 md:px-4">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center"
              data-testid="navbar-logo"
              onClick={() => setMobileMenuOpen(false)}
            >
              <img 
                src="/logo.png" 
                alt="MagicStay Homes" 
                className="h-12 md:h-14 w-auto object-contain hover:scale-105 transition-transform duration-200"
              />
            </Link>

            {/* Desktop Navigation - lg breakpoint (1024px) to prevent tablet overlap */}
            <div className="hidden lg:flex items-center space-x-1 xl:space-x-2 flex-wrap justify-end">
              <NavLinks />
              
              {/* Install App Button */}
              {!isInstalled && (
                <Button
                  onClick={handleInstallClick}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5 border-secondary text-secondary hover:bg-secondary hover:text-white transition-all ml-1 hover:scale-105 active:scale-95"
                  data-testid="install-app-button"
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden xl:inline">{i18n.language === 'pt-BR' ? 'Baixar App' : 'Get App'}</span>
                  <span className="xl:hidden">{i18n.language === 'pt-BR' ? 'App' : 'App'}</span>
                </Button>
              )}
            </div>

            {/* User Status & Actions */}
            <div className="flex items-center gap-2">
              {/* Language Selector */}
              <LanguageSelectorNew />
              
              {user ? (
                <div className="flex items-center gap-2">
                  {/* Online Status */}
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-200">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-xs font-medium text-green-700">{t('common.online')}</span>
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="flex items-center space-x-2 h-9" 
                        data-testid="navbar-user-menu"
                        aria-label={t('navigation.myAccount') || 'My Account'}
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center hover:scale-110 transition-transform">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-medium hidden sm:inline text-sm">{user.name}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 glass">
                      <div className="px-2 py-1.5 text-sm">
                        <div className="font-medium">{user.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                      </div>
                      <DropdownMenuItem onClick={() => { navigate('/minha-conta'); setMobileMenuOpen(false); }} data-testid="navbar-profile-link">
                        <User className="mr-2 h-4 w-4" />
                        {t('navigation.myAccount')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { navigate('/favorites'); setMobileMenuOpen(false); }} data-testid="navbar-favorites-link">
                        <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/>
                        </svg>
                        {t('navigation.favorites')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { navigate('/saved-searches'); setMobileMenuOpen(false); }} data-testid="navbar-saved-searches-link">
                        <Search className="mr-2 h-4 w-4" />
                        {t('navigation.savedSearches')}
                      </DropdownMenuItem>
                      {user.role !== 'admin' && (
                        <DropdownMenuItem onClick={() => { navigate('/host/dashboard'); setMobileMenuOpen(false); }}>
                          <Home className="mr-2 h-4 w-4" />
                          {t('navigation.dashboard')}
                        </DropdownMenuItem>
                      )}
                      {user.role === 'admin' && (
                        <DropdownMenuItem onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} data-testid="dropdown-admin-link">
                          <Settings className="mr-2 h-4 w-4" />
                          {i18n.language === 'pt' || i18n.language === 'pt-BR' ? 'Painel Admin' : 'Admin Dashboard'}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={handleLogout} data-testid="navbar-logout-button" className="text-red-600">
                        <LogOut className="mr-2 h-4 w-4" />
                        {t('navigation.logout')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 border border-gray-200">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span className="text-xs font-medium text-gray-600">{t('common.visitor')}</span>
                  </div>
                  <Link to="/login" data-testid="navbar-login-link">
                    <Button className="bg-primary text-white hover:bg-[#0B2246] h-9 text-sm font-semibold hover:scale-105 active:scale-95 transition-transform">
                        {t('navigation.login')}
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button - visible on screens smaller than lg (1024px) */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="lg:hidden" 
                    data-testid="mobile-menu-button"
                    aria-label={t('common.menu') || 'Menu'}
                  >
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent 
                  side="right" 
                  className="w-[280px] sm:w-[350px] glass !h-[100dvh] flex flex-col" 
                  aria-describedby={undefined}
                >
                  <div className="flex flex-col gap-6 mt-8 flex-1 overflow-y-auto pb-[max(env(safe-area-inset-bottom),24px)]">
                    {user && (
                      <div className="flex items-center gap-3 pb-4 border-b">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <User className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{user.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                          <div className="flex items-center gap-1 mt-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                            <span className="text-xs text-green-700">{t('common.online')}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex flex-col gap-4">
                      <Link
                        to="/search"
                        className="flex items-center gap-3 text-base font-medium hover:text-secondary transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Search className="h-5 w-5" />
                        {t('navigation.searchProperties')}
                      </Link>
                      
                      {user && user.role !== 'admin' && (
                        <Link
                          to="/host/dashboard"
                          className="flex items-center gap-3 text-base font-medium hover:text-secondary transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Home className="h-5 w-5" />
                          {t('navigation.myProperties')}
                        </Link>
                      )}
                      
                      {user && user.role === 'admin' && (
                        <Link
                          to="/admin"
                          className="flex items-center gap-3 text-base font-medium hover:text-secondary transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                          data-testid="mobile-admin-link"
                        >
                          <Settings className="h-5 w-5" />
                          {i18n.language === 'pt' || i18n.language === 'pt-BR' ? 'Painel Admin' : 'Admin Dashboard'}
                        </Link>
                      )}
                      
                      <Link
                        to="/host/create-property"
                        className="flex items-center gap-3 text-base font-medium hover:text-secondary transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Search className="h-5 w-5" />
                        {t('navigation.plans')}
                      </Link>
                      
                      <Link
                        to="/how-it-works"
                        className="flex items-center gap-3 text-base font-medium hover:text-secondary transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t('navigation.howItWorks')}
                      </Link>
                      
                      <Link
                        to="/faq"
                        className="flex items-center gap-3 text-base font-medium hover:text-secondary transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t('navigation.faq')}
                      </Link>

                      {/* Install App Button - Mobile */}
                      {!isInstalled && (
                        <>
                          <div className="border-t my-2"></div>
                          <button
                            onClick={() => {
                              handleInstallClick();
                              setMobileMenuOpen(false);
                            }}
                            className="flex items-center gap-3 text-base font-medium text-secondary hover:text-[#00a8b5] transition-colors bg-secondary/10 rounded-lg px-3 py-2 hover:scale-[1.02] active:scale-[0.98]"
                            data-testid="install-app-button-mobile"
                          >
                            <Smartphone className="h-5 w-5" />
                            {i18n.language === 'pt-BR' ? 'Instalar Aplicativo' : 'Install App'}
                          </button>
                        </>
                      )}

                      {user && (
                        <>
                          <div className="border-t my-2"></div>
                          <Link
                            to="/minha-conta"
                            className="flex items-center gap-3 text-base font-medium hover:text-secondary transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <User className="h-5 w-5" />
                            {t('navigation.myAccount')}
                          </Link>
                          <Link
                            to="/favorites"
                            className="flex items-center gap-3 text-base font-medium hover:text-secondary transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/>
                            </svg>
                            {t('navigation.favorites')}
                          </Link>
                          <Link
                            to="/saved-searches"
                            className="flex items-center gap-3 text-base font-medium hover:text-secondary transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Search className="h-5 w-5" />
                            {t('navigation.savedSearches')}
                          </Link>
                          <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 text-base font-medium text-red-600 hover:text-red-700 transition-colors"
                          >
                            <LogOut className="h-5 w-5" />
                            {t('navigation.logout')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer for fixed navbar */}
      <div className="h-14 md:h-16" />

      {/* Install PWA Modal */}
      <InstallPWAModal 
        open={showInstallModal} 
        onOpenChange={setShowInstallModal}
        deferredPrompt={deferredPrompt}
        setDeferredPrompt={setDeferredPrompt}
      />
    </>
  );
};
